/**
 * Identify mmdId from multiple sources: ADD data and requestData fallbacks.
 * 
 * Priority:
 * 1. ADD data (server-side)
 * 2. dataLayer vehicles dealerCodes (client-side context)
 * 3. PrivateAPI integrationConfigs (Asynchronous promise or resolved object)
 * 4. PrivateAPI digital-retailing dealerId (client-side context)
 * 
 * @param {Object} accountData - Data from fetchAddData (Server-side)
 * @param {Object} requestData - Contextual data from the request (Server or Client)
 * @returns {Promise<Object|null>} - Format: { type: 'mmd', code: string } or null
 */
export const getMmdId = async (accountData, requestData) => {
	// 1. Primary source: ADD data (server-side)
	const mmdFromAdd = accountData?.dealerCodes?.find(
		(code) => code.type === 'mmd'
	);
	if (mmdFromAdd?.code) {
		return { type: 'mmd', code: mmdFromAdd.code };
	}

	// Helper to resolve mmdId from a provider object (handles Promises as well)
	const resolveFromObject = async (obj) => {
		if (!obj) return null;

		// A. Check for dealerCodes array (Source 1 pattern)
		const mmdFromDealerCodes = obj?.dealerCodes?.find?.(
			(code) => code.type === 'mmd'
		);
		if (mmdFromDealerCodes?.code) return mmdFromDealerCodes.code;

		// B. Check dataLayer vehicles (Source 2 pattern)
		const mmdFromDataLayer = obj?.dataLayer?.vehicles?.[0]?.dealerCodes?.mmd;
		if (mmdFromDataLayer) return mmdFromDataLayer;

		// C. Check PrivateAPI integrationConfigs (Can be a Promise)
		let integrationConfigs = obj?.PrivateAPI?.integrationConfigs;
		if (integrationConfigs instanceof Promise) {
			try {
				integrationConfigs = await integrationConfigs;
			} catch (e) {
				integrationConfigs = null;
			}
		}
		const mmdFromIntegrationConfigs =
			integrationConfigs?.['cox-digital-retailing']?.fields?.mmdId ||
			integrationConfigs?.['digital-retailing']?.fields?.mmdId;
		if (mmdFromIntegrationConfigs) return mmdFromIntegrationConfigs;

		// D. Check PrivateAPI content (Can also be a Promise)
		let content = obj?.PrivateAPI?.content;
		if (content instanceof Promise) {
			try {
				content = await content;
			} catch (e) {
				content = null;
			}
		}
		const mmdFromDigitalRetailing =
			content?.['digital-retailing']?.dealerId ||
			content?.['digital-retailing']?.mmdId;
		if (mmdFromDigitalRetailing) return mmdFromDigitalRetailing;

		return null;
	};

	// 2. Try requestData (Unified source)
	const codeFromRequest = await resolveFromObject(requestData);
	if (codeFromRequest) {
		return { type: 'mmd', code: codeFromRequest };
	}

	// 3. Try window.DDC (Client-side fallback)
	if (typeof window !== 'undefined' && window.DDC) {
		const codeFromWindow = await resolveFromObject(window.DDC);
		if (codeFromWindow) {
			return { type: 'mmd', code: codeFromWindow };
		}
	}

	return null;
};
